import React, { useState } from "react";
import { getApiUrl } from "../helpers";
import { brandConfig } from "../brandConfig";
import "./DavesFeast.css";

const API_URL = getApiUrl();

interface FormData {
  vibe: string;
  seafood: string;
  flavours: string;
  grazing: string;
  dessert: string;
  drinks: string;
  dietary: string;
  anything_else: string;
}

interface QuestionConfig {
  name: keyof FormData;
  text: string;
  hint: string;
  type: "single";
  options: string[];
}

const questions: QuestionConfig[] = [
  {
    name: "vibe",
    text: "What's the vibe?",
    hint: "How do you picture the meal on the beach?",
    type: "single",
    options: [
      "Relaxed — barefoot, easy, no fuss",
      "Elevated — casual but spoil me a little",
      "Go all out — make it special",
    ],
  },
  {
    name: "seafood",
    text: "How do you feel about seafood?",
    hint: "We're on Moreton Bay after all...",
    type: "single",
    options: [
      "Love it — the more the better",
      "Enjoy it but mix it with some meat too",
      "I'm fussy — only certain seafood",
      "Not really my thing",
    ],
  },
  {
    name: "flavours",
    text: "What flavours speak to you?",
    hint: "If you were picking a restaurant, what kind of menu excites you?",
    type: "single",
    options: [
      "Fresh and light — citrus, herbs, clean",
      "Rich and indulgent — butter, garlic, slow-cooked",
      "A bit of spice and punch",
      "Classic and simple — let the ingredients shine",
      "Surprise me",
    ],
  },
  {
    name: "grazing",
    text: "Before the main event...",
    hint: "What kind of grazing makes you happiest?",
    type: "single",
    options: [
      "Oysters, prawns, something from the sea",
      "Cheese, charcuterie, bread — the classics",
      "Light and fresh — dips, salads, crudités",
      "A bit of everything",
    ],
  },
  {
    name: "dessert",
    text: "Sweet tooth?",
    hint: "How do you want to finish?",
    type: "single",
    options: [
      "Yes — something indulgent",
      "Something light and fruity",
      "Cheese board over dessert any day",
      "Birthday cake — it's tradition!",
    ],
  },
  {
    name: "drinks",
    text: "What will you be drinking?",
    hint: "Helps us match the food to the mood.",
    type: "single",
    options: [
      "Bubbles",
      "White wine or rosé",
      "Red wine",
      "Beer",
      "A mix of everything",
    ],
  },
];

const BeachDinner: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    vibe: "",
    seafood: "",
    flavours: "",
    grazing: "",
    dessert: "",
    drinks: "",
    dietary: "",
    anything_else: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleSingleSelect = (name: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    const lines: string[] = [];
    questions.forEach((q, i) => {
      const val = formData[q.name];
      lines.push(`Q${i + 1}. ${q.text}: ${val || "(no selection)"}`);
    });
    if (formData.dietary) {
      lines.push(`\nDietary requirements / no-go foods: ${formData.dietary}`);
    }
    if (formData.anything_else) {
      lines.push(`\nAnything else: ${formData.anything_else}`);
    }

    const fullMessage = lines.join("\n");

    const query =
      "mutation SubmitContactForm($input: SubmitContactFormInput!) { submitContactForm(input: $input) { id email } }";

    const variables = {
      input: {
        firstName: "Margaret",
        lastName: "(Birthday Beach Feast Form)",
        message: fullMessage,
        formType: "contact",
        submissionUrl: window.location.href,
        notificationEmails: ["tom@tommillerservices.com", "hamishrmiller@gmail.com"],
      },
    };

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-id": brandConfig.tenantId,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();
      if (result.errors) throw new Error(result.errors[0].message);

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Submit error:", err);
      setShowError(true);
      setTimeout(() => setShowError(false), 4000);
      setSubmitting(false);
    }
  };

  const isSelected = (name: keyof FormData, value: string): boolean => {
    return formData[name] === value;
  };

  return (
    <div className="feast-page">
      <div className="feast-container">
        {!submitted ? (
          <>
            <div className="feast-header">
              <div className="feast-brand">Moments Brisbane</div>
              <h1>
                Margaret's <em>Birthday</em>
              </h1>
              <p className="intro" style={{ fontStyle: "italic", marginBottom: 16 }}>
                A Beach Feast on Moreton Bay
              </p>
              <div className="feast-divider">&#x2726;</div>
              <p className="intro">
                Happy Birthday Margaret! We're taking a boat out to a sandy beach
                on Moreton Bay for a special celebration — BBQ on the sand, fresh
                seafood, cold plates, and good company. Answer a few quick questions
                so we can design the perfect menu for your day.
              </p>
            </div>

            <div>
              {questions.map((q, i) => (
                <div className="feast-section" key={q.name}>
                  <div className="feast-section-label">Question {String(i + 1).padStart(2, "0")}</div>
                  <div className="feast-question">
                    <div className="feast-question-text">{q.text}</div>
                    <div className="feast-question-hint">{q.hint}</div>
                    <div className="feast-options">
                      {q.options.map((opt) => (
                        <div
                          key={opt}
                          className={`feast-option${isSelected(q.name, opt) ? " selected" : ""}`}
                          onClick={() => handleSingleSelect(q.name, opt)}
                        >
                          <span className="check"></span>
                          <span className="feast-option-label">{opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}

              {/* Dietary */}
              <div className="feast-section">
                <div className="feast-section-label">Question 07</div>
                <div className="feast-question">
                  <div className="feast-question-text">
                    Any dietary requirements or no-go foods?
                  </div>
                  <div className="feast-question-hint">
                    For you or any of the guests — allergies, intolerances, things you can't stand.
                  </div>
                  <textarea
                    className="feast-free-text"
                    placeholder="e.g. 'One guest is gluten free, I hate coriander, nobody likes blue cheese...'"
                    value={formData.dietary}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        dietary: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Anything else */}
              <div className="feast-section">
                <div className="feast-section-label">Question 08</div>
                <div className="feast-question">
                  <div className="feast-question-text">Anything else?</div>
                  <div className="feast-question-hint">
                    Dream dishes, favourite foods, childhood memories — tell us anything that helps.
                  </div>
                  <textarea
                    className="feast-free-text"
                    placeholder="The more we know, the better we can make your day..."
                    value={formData.anything_else}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        anything_else: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="feast-submit-area">
                <button
                  className="feast-submit-btn"
                  disabled={submitting}
                  onClick={handleSubmit}
                >
                  {submitting ? "Sending..." : "Send My Choices"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="feast-success-message visible">
            <div className="feast-divider">&#x2726;</div>
            <h2>
              Thank <em>You</em>, Margaret
            </h2>
            <p>
              Your choices have been received. We'll design the perfect
              <br />
              beach feast based on your preferences and be in touch
              <br />
              very soon. Happy Birthday!
            </p>
            <div className="feast-divider" style={{ marginTop: 40 }}>
              &#x2726;
            </div>
          </div>
        )}
      </div>

      <div className={`feast-error-toast${showError ? " visible" : ""}`}>
        Something went wrong — please try again.
      </div>
    </div>
  );
};

export default BeachDinner;
